// Hydration helpers for prerendered public pages (/p/helpdesk/**).
//
// Prerendered HTML is captured after the page has fetched its data, so the
// first client render must reproduce that exact markup or React discards it
// (minified errors #418/#423). Two tools make that possible:
//
// 1. A JSON snapshot of the loaded data, rendered into the page as
//    <script type="application/json" id=…>, captured by prerender, and read
//    back to seed state on the client's first render.
// 2. useClientReady(): false during the hydration render (and forever while
//    prerendering), true afterwards. Client-only values — stored theme, view
//    preference, locale/time-relative dates — use deterministic defaults until
//    it flips, so prerender and the first client render agree.

import { createElement, useEffect, useState } from 'react';

/** Set by scripts/prerender.mjs before any page script runs. */
export const IS_PRERENDER =
  typeof window !== 'undefined' && (window as unknown as { __KORA_PRERENDER__?: boolean }).__KORA_PRERENDER__ === true;

let hydrated = false;

/** False on the hydration render (and always while prerendering); true once
 *  mounted. Components mounted after the first hydration start as true. */
export function useClientReady(): boolean {
  const [ready, setReady] = useState(() => hydrated && !IS_PRERENDER);
  useEffect(() => {
    if (IS_PRERENDER) return;
    hydrated = true;
    setReady(true);
  }, []);
  return ready;
}

/** Read a snapshot rendered by <HydrationSnapshot>; null unless its key matches. */
export function readSnapshot<T extends { key: string }>(id: string, key: string): T | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(id);
  if (!el?.textContent) return null;
  try {
    const data = JSON.parse(el.textContent) as T;
    return data && data.key === key ? data : null;
  } catch {
    return null;
  }
}

/** Inline JSON snapshot (escaped so it can never close the script tag). */
export function HydrationSnapshot({ id, data }: { id: string; data: unknown }) {
  const json = JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/[\u2028\u2029]/g, (c) => `\\u${c.charCodeAt(0).toString(16)}`);
  return createElement('script', { type: 'application/json', id, dangerouslySetInnerHTML: { __html: json } });
}

/** Locale- and timezone-independent date ("Oct 5, 2026") — identical in
 *  prerender and every visitor's first render. */
export function stableDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
